import { Bi } from './Bilingual';
import { SpeakButton } from './SpeakButton';
import { useSettings } from '../state/SettingsContext';
import { useTts } from '../state/TtsContext';
import { useT } from '../hooks/useT';

/** Shown on the answer chips. */
export const ARABIC_LETTERS = ['أ', 'ب', 'ج', 'د', 'هـ', 'و', 'ز', 'ح'];
export const ENGLISH_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Said out loud instead of the bare letter.
 *
 * "د." is the ordinary Arabic abbreviation for "doctor", and speech engines
 * read it that way — answer D was announced as "الدكتور. المشاة فقط…". A single
 * letter before a full stop also tends to be swallowed entirely. Spelling the
 * letter's name, followed by a comma rather than a full stop, is read correctly:
 * checked against every option by transcribing the audio back.
 */
export const ARABIC_LETTER_NAMES = ['أَلِف', 'باء', 'جيم', 'دال', 'هاء', 'واو', 'زاي', 'حاء'];

/** Exactly what is read aloud for one answer. */
export function spokenAnswer(index: number, text: string, lang: 'ar' | 'en'): string {
  if (lang === 'en') return `${ENGLISH_LETTERS[index] ?? index + 1}. ${text}`;
  return `${ARABIC_LETTER_NAMES[index] ?? index + 1}، ${text}`;
}

export type AnswerState = 'idle' | 'selected' | 'correct' | 'wrong' | 'dim';

interface AnswerButtonProps {
  index: number;
  textAr: string;
  textEn: string;
  state: AnswerState;
  disabled: boolean;
  onSelect: () => void;
}

export function AnswerButton({ index, textAr, textEn, state, disabled, onSelect }: AnswerButtonProps) {
  const t = useT();
  const { uiLang } = useSettings();
  const { status } = useTts();
  const letters = uiLang === 'ar' ? ARABIC_LETTERS : ENGLISH_LETTERS;
  const letter = letters[index] ?? String(index + 1);
  const speakingThis = status.currentItemId === `answer-${index}` && status.state !== 'idle';

  const mark = state === 'correct' ? '✅' : state === 'wrong' ? '❌' : null;
  const markLabel = state === 'correct' ? t('correct') : state === 'wrong' ? t('incorrect') : null;

  const classes = [
    'answer',
    state === 'selected' ? 'answer--selected' : '',
    state === 'correct' ? 'answer--correct' : '',
    state === 'wrong' ? 'answer--wrong' : '',
    state === 'dim' ? 'answer--dim' : '',
    speakingThis ? 'answer--speaking' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <li className={classes}>
      <button type="button" className="answer__main" onClick={onSelect} disabled={disabled}>
        <span className="answer__letter" aria-hidden="true">
          {letter}
        </span>
        <span className="answer__body">
          <Bi ar={textAr} en={textEn} as="span" />
        </span>
        {mark ? (
          <span className="answer__mark">
            <span aria-hidden="true">{mark}</span>
            <span className="visually-hidden">{markLabel}</span>
          </span>
        ) : null}
      </button>
      <SpeakButton
        id={`answer-${index}`}
        text={spokenAnswer(index, textAr, 'ar')}
        textEn={spokenAnswer(index, textEn, 'en')}
        label={`${t('listenAnswer')} ${letter}`}
      />
    </li>
  );
}
